import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaFacebookF, FaLinkedinIn, FaTwitter, FaInstagram, FaPhone, FaEnvelope } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import MigrantLogo from '/assets/Migrant-logo.png';

const shimmer = keyframes`
  0% { background-position: -500px 0; }
  100% { background-position: 500px 0; }
`;

const FooterContainer = styled.footer`
  background-color: white;
  color: #333;
  font-family: 'Poppins', sans-serif;
  position: relative;
  overflow: hidden;
  margin-top: 3rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #000000;
    background-size: 1000px 100%;
    animation: ${shimmer} 8s infinite linear;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  
  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FooterSection = styled.div`
  h4 {
    font-size: 1.5rem;
    margin-bottom: 1.2rem;
    color: #00A6D4;
    font-weight: 600;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 2px;
      background: #00A6D4;
      border-radius: 2px;
      transition: width 0.3s ease;
    }
  }
`;

const LogoSection = styled.div`
  margin-bottom: 1rem;

  img {
    max-width: 160px;
    height: auto;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const FooterText = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.2rem;
  color: #000;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: 1.5rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #00A6D4;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #06b99c;
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContactItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #333;
  
  svg {
    min-width: 18px;
    color: white;
    background-color: #00A6D4;
    border-radius: 50%;
    padding: 0.4rem;
    font-size: 0.9rem;
  }
`;

const LinksList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: 0.8rem;
  
  a {
    color: #333;
    text-decoration: none;
    font-size: 0.9rem;
    display: inline-block;
    position: relative;
    transition: transform 0.2s ease;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 1px;
      background-color: #08CDAE;
      transition: width 0.3s ease;
    }
    
    &:hover {
      color: #08CDAE;
      transform: translateX(5px);
      
      &::after {
        width: 100%;
      }
    }
  }
`;

const CopyrightSection = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  margin-top: 2rem;
  background: #000000;
  color: white;
  font-size: 0.8rem;
  
  p {
    margin: 0.5rem 0;
  }
  
  a {
    color: white;
    text-decoration: none;
    position: relative;
    padding: 0 5px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 1px;
      background-color: white;
      transition: width 0.3s ease;
    }
    
    &:hover::after {
      width: 100%;
    }
  }
`;

const ContactLink = styled.a`
  color: #333;
  text-decoration: none;
  
  &:hover {
    color: #08CDAE;
    text-decoration: underline;
  }
`;

const SubscribeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const FormInput = styled.input`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #08CDAE;
    box-shadow: 0 0 0 2px rgba(8, 205, 174, 0.2);
  }
`;

const FormButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: #00A6D4;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  
  &:hover {
    background-color: #06b99c;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  color: #333;
  margin-bottom: -0.5rem;
`;

const GDPRCheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #333;
  line-height: 1.4;
  cursor: pointer;
  
  input[type="checkbox"] {
    margin-top: 0.2rem;
    width: 16px;
    height: 16px;
    accent-color: #08CDAE;
    cursor: pointer;
  }
  
  a {
    color: #08CDAE;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FormSuccess = styled.div`
  padding: 0.8rem;
  background-color: #d4edda;
  color: #155724;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = React.useState('');
  const [gdprConsent, setGdprConsent] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to Netlify
    // The form will be handled by Netlify's form detection
    setSubmitted(true);
    setEmail('');
    setGdprConsent(false);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <LogoSection>
              <img src={MigrantLogo} alt="RETASLogo" />
            </LogoSection>
            <FooterText>
              Established in 2002, RETAS (Refugee Education Training Advice Service), is a well-respected community-based charity that supports the social, cultural and economic integration of refugees and asylum seekers in West Yorkshire.
            </FooterText>
            <SocialLinks>
              <SocialIcon href="https://www.facebook.com/retas.leeds.3/" aria-label="Facebook">
                <FaFacebookF />
              </SocialIcon>
              <SocialIcon href="https://www.linkedin.com/company/retas-leeds/" aria-label="LinkedIn">
                <FaLinkedinIn />
              </SocialIcon>
              <SocialIcon href="https://www.instagram.com/retas.leeds/" aria-label="Instagram">
                <FaInstagram />
              </SocialIcon>
            </SocialLinks>
          </FooterSection>
          
          <FooterSection>
            <h4>Contact Us</h4>
            <ContactList>
              <ContactItem>
                <HiLocationMarker />
                <span>233-237 Roundhay Road, Leeds, LS8 4HS</span>
              </ContactItem>
              <ContactItem>
                <FaPhone />
                <ContactLink href="tel: 01133805630"> 0113 380 5630</ContactLink>
              </ContactItem>
              <ContactItem>
                <FaEnvelope />
                <ContactLink href="mailto:info@retasleeds.org.uk">info@retasleeds.org.uk</ContactLink>
              </ContactItem>
            </ContactList>
          </FooterSection>
          
          <FooterSection>
            <h4>Quick Links</h4>
            <LinksList>
              <LinkItem><a href="/">Home</a></LinkItem>
              <LinkItem><a href="/about-us">About Us</a></LinkItem>
              <LinkItem><a href="/what-we-do">What We Do</a></LinkItem>
              <LinkItem><a href="/i-need-help">I Need Help</a></LinkItem>
              <LinkItem><a href="/get-involved">Get Involved</a></LinkItem>
              <LinkItem><a href="/library">Library</a></LinkItem>
              <LinkItem><a href="/contact-us">Contact Us</a></LinkItem>
            </LinksList>
          </FooterSection>

          <FooterSection>
            <h4>Subscribe to Our Newsletter</h4>
            <FooterText>
              Stay updated with our latest news and events by subscribing to our newsletter.
            </FooterText>
            <SubscribeForm 
              name="newsletter" 
              method="POST" 
              data-netlify="true"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="form-name" value="newsletter" />
              <FormLabel htmlFor="email">Email Address</FormLabel>
              <FormInput
                type="email"
                id="email"
                name="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              
              <GDPRCheckboxLabel>
                <input 
                  type="checkbox" 
                  name="gdpr-consent"
                  checked={gdprConsent}
                  onChange={(e) => setGdprConsent(e.target.checked)}
                  required 
                />
                <span>
                  I agree to receive emails from RETAS Leeds and understand that I can unsubscribe at any time. 
                  For privacy inquiries, please contact us at <a href="mailto:info@retasleeds.org.uk">info@retasleeds.org.uk</a>.
                </span>
              </GDPRCheckboxLabel>
              
              <FormButton type="submit">Subscribe</FormButton>
              {submitted && (
                <FormSuccess>
                  Thank you for subscribing! You'll hear from us soon.
                </FormSuccess>
              )}
            </SubscribeForm>
          </FooterSection>
        </FooterGrid>
      </FooterContent>
      
      <CopyrightSection>
        <p>RETAS is a registered Charity. Charity Number: 1121330 - Company Number 6365085</p>
        <p>© {currentYear} All right reserved RETAS Leeds</p>
        <p><a href="/">Privacy Policy</a> | <a href="/">Terms</a></p>
      </CopyrightSection>
    </FooterContainer>
  );
};

export default Footer;